import React, { useRef, useState, useEffect } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Table, Input, Button, Space } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from "axios";
import "./data_customer_master.css";
import { useNavigate } from "react-router-dom";

interface CustomerDataType {
    key: string;
    cs_code: string;
    cs_name: string;
    customer_type_description: string;
    address_line1: string;
    address_line2: string;
    no_hp1: string;
    dealer_name: string;
}

interface FullCustomerDataType {
    cs_code: string;
    record_status?: string;
    customer_type?: string;
    id_type?: string;
    id_no?: string;
    title_p?: string;
    cs_name?: string;
    title_s?: string;
    search_kw?: string;
    address_line1?: string;
    address_line2?: string;
    rt_rw?: string;
    provinsi?: string;
    kabupaten?: string;
    kecamatan?: string;
    kelurahan?: string;
    kode_pos?: string;
    email?: string;
    no_hp1?: string;
    no_hp2?: string;
    termofpayment?: string;
    taxinvoicetype?: string;
    home_address_line1?: string;
    home_address_line2?: string;
    home_rtrw?: string;
    homeprovinsi?: string;
    homekabupaten?: string;
    homekecamatan?: string;
    homekelurahan?: string;
    homekodepos?: string;
    home_phone?: string;
    homefax_number?: string;
    homehp?: string;
    office_address_line1?: string;
    office_address_line2?: string;
    office_rtrw?: string;
    officeprovinsi?: string;
    officekabupaten?: string;
    officekecamatan?: string;
    officekelurahan?: string;
    officekodepos?: string;
    officetelpno?: string;
    officefaxno?: string;
    officephoneno?: string;
    vatnpwp?: string;
    vatnpwpdate?: string;
    vatpkp?: boolean;
    vatpkpdate?: string;
    vattransactioncode?: string;
    vatname?: string;
    vat_address1?: string;
    vat_address2?: string;
    vat_rtrw?: string;
    vatprovinsi?: string;
    vatkabupaten?: string;
    vatkecamatan?: string;
    vatkelurahan?: string;
    vatkodepos?: string;
    vatkppcode?: string;
    vatkppdeskripsi?: string;
    businesstype?: string;
    companygroup?: string;
    website?: string;
    customerclass?: string;
    customerbehaviour?: string;
    gender?: string;
    birthday?: string;
    religion?: string;
    maritalstatus?: string;
    marriagedate?: string;
    jobtitle?: string;
    hobby?: string;
    note?: string;
    correspondence_equal_to?: string;
    billing_equal_to?: string;
    printstate?: boolean;
    credit_limit?: number;
    remaining_credit_limit?: number;
    dealername?: string;
    collect?: string;
    dealersalesrep?: string;
    contactdate?: string;
}

type DataIndex = keyof CustomerDataType;
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const DataCustomerMaster: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);
    const [customerData, setCustomerData] = useState<CustomerDataType[]>([]);
    const navigate = useNavigate();

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps["confirm"],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<CustomerDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (char) => char.toUpperCase())}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}>
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}>
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}>
                        close
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
        onFilter: (value, record) => { 
            const cellValue = record[dataIndex]; 
            const displayValue = cellValue !== null && cellValue !== undefined ? String(cellValue) : ""; 
            return displayValue.toLowerCase().includes((value as string).toLowerCase()); 
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },

        render: (text) => (
            <span
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                }}
            >
                {searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                )}
            </span>
        ),
    });

    const columns: TableColumnsType<CustomerDataType> = [
        {
            title: "Customer Code",
            dataIndex: "cs_code",
            key: "cs_code",
            width: "15%",
            ...getColumnSearchProps("cs_code"),
            sorter: (a: any, b: any) => (a.cs_code || '').localeCompare(b.cs_code || ''),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Customer Name",
            dataIndex: "cs_name",
            key: "cs_name",
            width: "20%",
            ...getColumnSearchProps("cs_name"),
            sorter: (a: any, b: any) => (a.cs_name || '').localeCompare(b.cs_name || ''),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Customer Type",
            dataIndex: "customer_type_description",
            key: "customer_type_description",
            width: "15%",
            ...getColumnSearchProps("customer_type_description"),
            sorter: (a: any, b: any) => (a.customer_type_description || '').localeCompare(b.customer_type_description || ''),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Address 1",
            dataIndex: "address_line1",
            key: "address_line1",
            width: "20%",
            ...getColumnSearchProps("address_line1"),
            sorter: (a: any, b: any) => (a.address_line1 || '').localeCompare(b.address_line1 || ''),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Address 2",
            dataIndex: "address_line2",
            key: "address_line2",
            width: "20%",
            ...getColumnSearchProps("address_line2"),
            sorter: (a: any, b: any) => (a.address_line2 || '').localeCompare(b.address_line2 || ''),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Phone",
            dataIndex: "no_hp1",
            key: "no_hp1",
            width: "10%",
            ...getColumnSearchProps("no_hp1"),
            sorter: (a: any, b: any) => (a.no_hp1 || '').localeCompare(b.no_hp1 || ''), 
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Dealer",
            dataIndex: "dealer_name",
            key: "dealer_name",
            width: "15%",
            ...getColumnSearchProps("dealer_name"),
            sorter: (a: any, b: any) => (a.dealer_name || '').localeCompare(b.dealer_name || ''),
            sortDirections: ["ascend", "descend"],
        },
    ];

    useEffect(() => {
        const fetchCustomerData = async () => {
            try {
                const response = await axios.get<CustomerDataType[]>(
                    `${backendUrl}/api/customer-master/all-data`,
                );
                setCustomerData(response.data.map((item) => ({ ...item, key: item.cs_code })));
            } catch (error) {
                console.error("Failed to fetch customer data:", error);
            }
        };
        fetchCustomerData();
    }, []);

    return (
        <div id="iypr" style={transformStyle}>
            <div className="gjs-grid-row" id="i8pk">
                <div className="gjs-grid-column" id="ixqo">
                    <Breadcrumb
                        items={[
                            {
                                title: (
                                    <>
                                        <UserOutlined />
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Customer</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/customer_master",
                                title: "Customer Master",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="it2k4">
                <div className="gjs-grid-column" id="i5oj">
                    <Table<CustomerDataType>
                        columns={columns}
                        dataSource={customerData}
                        pagination={{ pageSize: 10 }}
                        bordered
                        className="customer-data-table"
                        onRow={(record) => {
                            return {
                                onDoubleClick: async () => {
                                    try {
                                        const response = await axios.get<FullCustomerDataType>(
                                            `${backendUrl}/api/customer-master/${record.cs_code}`
                                        );
                                        const fullCustomerData = response.data;
                                        sessionStorage.setItem('selectedCustomerData', JSON.stringify(fullCustomerData));
                                        navigate('/dashboard/customer_master');
                                    } catch (error) {
                                        console.error("Failed to fetch full customer data:", error);
                                        alert("Gagal memuat detail pelanggan. Silakan coba lagi.");
                                    }
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataCustomerMaster;
